import 'twin.macro';
import tw from 'twin.macro';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import Input from 'components/form/input/Input';
import Button from 'components/button/Button';
import useCampaignValues from 'hooks/useCampaignValues';
import { useMutation } from 'hooks/sympl-mutation';
import { useToastNotifications } from 'hooks/notificationHooks';
import { ToastTypes } from 'types/notificationTypes';
import { SAVE_AD_VALUES } from 'graphql/campaign-values/mutations';
import {
  CampaignValuesState,
  copyOfferText,
  FIELD_LABEL,
  fieldHint,
  FieldValue,
  isValueDirty,
  MAX_LENGTH,
  MODE_LABEL,
  resetLinkText,
  savedValue,
  shownText,
  toPayload,
  ValueField,
  valueFromTyped,
} from 'utils/campaignValues';

/**
 * SYM-6351 (scenes 1-5, 9). The campaign's Job title and Location, at the top of the Template
 * content panel. ONE value for the whole campaign: saving it redraws every ad that is not published
 * yet (D1, D5, D8), so these rows save on their own, not with the ad (S3-d) -- an unsaved row is
 * discarded when the panel closes, never saved along with an ad by accident.
 *
 * Which rows show is the design's own declaration (X-5), passed in by TemplateValueControls.
 */
const CampaignValueRows = ({ fields }: { fields: ValueField[] }) => {
  const { state, vacancyId, refetch } = useCampaignValues();

  if (!state || !vacancyId || !fields.length) return null;

  return (
    <div tw="flex flex-col gap-4">
      {fields.map((field) => (
        <ValueRow
          key={field}
          field={field}
          state={state}
          vacancyId={vacancyId}
          onSaved={refetch}
        />
      ))}
    </div>
  );
};

const ValueRow = ({
  field,
  state,
  vacancyId,
  onSaved,
}: {
  field: ValueField;
  state: CampaignValuesState;
  vacancyId: number;
  onSaved: () => unknown;
}) => {
  const { addToast } = useToastNotifications();
  const saved = savedValue(field, state);
  const [draft, setDraft] = useState<FieldValue>(saved);
  const [typed, setTyped] = useState<string>(shownText(field, saved, state));

  // A new answer from the server (a save, or targeting changed) resets the row.
  const savedKey = JSON.stringify([saved, shownText(field, saved, state)]);
  useEffect(() => {
    setDraft(saved);
    setTyped(shownText(field, saved, state));
  }, [savedKey]);

  const [save, { loading }] = useMutation<
    { saveAdValues: { ad_values: unknown } },
    { vacancyId: number; input: Partial<Record<ValueField, FieldValue>> }
  >(SAVE_AD_VALUES);

  const send = async (value: FieldValue) => {
    try {
      await save({
        variables: { vacancyId, input: { [field]: toPayload(value) } },
      });
      // The server redrew the unpublished ads; the editor fetches their new pictures when this
      // answer changes (AdBuilder) and draws the open ad's tiles afresh (applyStoredPictures).
      await onSaved();
    } catch (_) {
      addToast({
        type: ToastTypes.ERROR,
        description: `The ${FIELD_LABEL[
          field
        ].toLowerCase()} could not be saved.`,
      });
    }
  };

  const dirty = isValueDirty(saved, draft);
  const offer = copyOfferText(field, state);
  const fieldId = useMemo(() => uuid(), []);
  const limit = MAX_LENGTH[field];

  return (
    <div tw="flex flex-col gap-1" data-testid={`campaign-value-${field}`}>
      <div tw="flex items-center justify-between gap-2">
        <label htmlFor={fieldId} tw="text-sm font-semibold">
          {FIELD_LABEL[field]}
        </label>
        <span
          css={[
            tw`text-xs px-2 py-0.5 rounded-full`,
            draft.mode === 'automatic'
              ? tw`bg-green-100 text-green-800`
              : draft.mode === 'custom'
              ? tw`bg-indigo-100 text-indigo-800`
              : tw`bg-gray-100 text-gray-700`,
          ]}
        >
          {MODE_LABEL[draft.mode]}
        </span>
      </div>
      <Input
        id={fieldId}
        enableEmoji={false}
        inputStyles={tw`w-full`}
        value={typed}
        maxLength={limit}
        placeholder={draft.mode === 'hidden' ? 'empty' : undefined}
        onChange={({ target }) => {
          const next = target.value.slice(0, limit);
          setTyped(next);
          setDraft(valueFromTyped(field, next, state));
        }}
      />
      <p tw="text-xs text-gray-500">
        {fieldHint(field, draft.mode)}{' '}
        <span tw="text-gray-400">
          {typed.length}/{limit}
        </span>
      </p>

      {dirty && (
        <div tw="flex gap-2 mt-1">
          <Button loading={loading} onClick={() => send(draft)}>
            Save for all ads
          </Button>
          <Button
            variant="secondary"
            disabled={loading}
            onClick={() => {
              setDraft(saved);
              setTyped(shownText(field, saved, state));
            }}
          >
            Cancel
          </Button>
        </div>
      )}

      {!dirty && saved.mode !== 'automatic' && (
        <button
          type="button"
          tw="text-sm text-indigo-600 underline text-left"
          disabled={loading}
          onClick={() => send({ mode: 'automatic', text: null })}
        >
          {resetLinkText(field, state)}
        </button>
      )}

      {!dirty && offer && (
        <div tw="mt-1 p-2 rounded bg-indigo-50 text-sm text-gray-700 flex flex-col gap-1">
          <span>{offer.text}</span>
          <button
            type="button"
            tw="text-indigo-600 underline text-left font-semibold"
            disabled={loading}
            onClick={() => send(offer.value)}
          >
            {offer.button}
          </button>
        </div>
      )}
    </div>
  );
};

export default CampaignValueRows;
